/* Draft-only enhancements for captured public markup; no third-party scripts. */
(() => {
  'use strict';
  const all = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const one = (selector, scope = document) => scope.querySelector(selector);
  const desktop = window.matchMedia('(min-width: 769px)');
  const wide = window.matchMedia('(min-width: 1024px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scriptURL = document.currentScript?.src || all('script[src]').find(script => script.src.includes('/reference-site.js'))?.src;
  const compareDataURL = scriptURL ? new URL('reference-compare-products.json', scriptURL).href : null;
  const isDraft = () => [document.documentElement, document.body].some(element => element?.hasAttribute('data-reference-draft') && element.getAttribute('data-reference-draft') !== 'false');
  const checkoutEnabled = () => document.documentElement.getAttribute('data-reference-checkout-enabled') === 'true';
  let lastFocus = null;
  let noticeTimer;

  function notice(message, trigger) {
    let element = document.getElementById('reference-preview-notice');
    if (!element) {
      element = document.createElement('div');
      element.id = 'reference-preview-notice';
      element.className = 'reference-preview-notice';
      element.setAttribute('role', 'alert');
      const messageNode = document.createElement('span');
      messageNode.className = 'reference-preview-notice__message';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'reference-preview-notice__close';
      close.setAttribute('aria-label', 'Fermer le message');
      close.textContent = '×';
      close.addEventListener('click', () => { element.hidden = true; lastFocus?.focus?.(); });
      element.append(messageNode, close);
      document.body.append(element);
    }
    lastFocus = trigger || document.activeElement;
    one('.reference-preview-notice__message', element).textContent = message;
    element.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { element.hidden = true; }, 9000);
  }

  function checkoutURL(value) {
    if (!value) return false;
    try {
      const url = new URL(value, location.href);
      return /^\/(?:checkout|checkouts)(?:\/|$)/i.test(url.pathname)
        || (/(?:^|\.)checkoutflow\.(?:app|base44\.app)$/i.test(url.hostname) && /^\/checkout(?:\/|$)/i.test(url.pathname));
    } catch { return false; }
  }

  function checkoutControl(control) {
    if (!control) return false;
    if (checkoutURL(control.getAttribute('href')) || checkoutURL(control.getAttribute('formaction'))) return true;
    if (control.matches('[name="checkout"], [data-checkout], [data-checkout-button], [data-checkoutflow], [data-checkoutflow-button], .shopify-payment-button__button, [data-reference-checkout-disabled]')) return true;
    const cart = control.closest('[data-cart-drawer], #CartDrawer, [data-cart-page], .cart-page, .cart-summary, .cart-drawer__footer, .drawer--cart');
    return !!cart && /^(?:commander|passer (?:la|ma) commande|procéder au paiement|proceder au paiement|payer|checkout|valider (?:mon|le) panier)\b/i.test(control.textContent.trim());
  }

  // Window capture wins over retained cart/document listeners. Product links are not checkout controls.
  window.addEventListener('click', event => {
    if (!isDraft() || checkoutEnabled() || !(event.target instanceof Element)) return;
    const control = event.target.closest('a,button,input[type="submit"],[role="button"]');
    if (!checkoutControl(control)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    notice('Aperçu du brouillon : le paiement est désactivé. Aucune commande ne sera créée.', control);
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const disabledForm = form.hasAttribute('data-reference-disabled-form');
    const checkout = isDraft() && !checkoutEnabled() && (checkoutURL(form.action) || checkoutControl(event.submitter));
    if (!disabledForm && !checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    notice(disabledForm ? 'Formulaire en aperçu : aucune information n’est envoyée ni enregistrée.' : 'Aperçu du brouillon : le paiement est désactivé. Aucune commande ne sera créée.', event.submitter);
  }, true);

  function setDetails(details, open) {
    details.open = open;
    details.classList.toggle('is-open', open);
    details.classList.remove('is-closing');
    one(':scope > summary', details)?.setAttribute('aria-expanded', String(open));
  }

  function initMenus(scope) {
    all('main-menu', scope).forEach(menu => {
      if (menu.dataset.referenceMenuReady) return;
      menu.dataset.referenceMenuReady = 'true';
      const disclosure = one(':scope > .main-menu__disclosure', menu);
      const toggle = disclosure && one(':scope > summary', disclosure);
      if (!disclosure || !toggle) return;
      const primary = all('.main-nav > li > details', menu);
      const nested = all('nav-menu > details', menu);
      let closeTimer;
      const position = () => {
        const header = menu.closest('store-header, .cc-header');
        const bottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
        document.documentElement.style.setProperty('--reference-header-end', `${bottom}px`);
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty('--viewport-width', `${document.documentElement.clientWidth}px`);
      };
      const closeChildren = except => primary.forEach(item => { if (item !== except) setDetails(item, false); });
      const close = (restoreFocus = false) => {
        closeChildren();
        if (!desktop.matches) setDetails(disclosure, false);
        document.body.classList.remove('reference-menu-open');
        if (restoreFocus) toggle.focus();
      };
      const sync = () => {
        closeChildren();
        setDetails(disclosure, desktop.matches);
        disclosure.classList.toggle('is-open', !desktop.matches && disclosure.open);
        nested.forEach(item => setDetails(item, desktop.matches));
        document.body.classList.remove('reference-menu-open');
        position();
      };
      toggle.addEventListener('click', event => {
        event.preventDefault();
        if (desktop.matches) return;
        position();
        const open = !disclosure.open;
        setDetails(disclosure, open);
        document.body.classList.toggle('reference-menu-open', open);
        if (!open) closeChildren();
      });
      primary.forEach(item => {
        const summary = one(':scope > summary', item);
        if (!summary) return;
        item.addEventListener('mouseenter', () => { if (desktop.matches) { clearTimeout(closeTimer); closeChildren(item); setDetails(item, true); } });
        item.addEventListener('mouseleave', () => { if (desktop.matches) closeTimer = setTimeout(() => setDetails(item, false), 130); });
        summary.addEventListener('click', event => {
          const link = event.target.closest('a');
          if (desktop.matches && link && !event.target.closest('svg')) return;
          event.preventDefault();
          const open = !item.open;
          closeChildren(item);
          setDetails(item, open);
          if (open && !desktop.matches) one('.main-nav__child', item)?.scrollTo({ top: 0 });
        });
        item.addEventListener('focusout', () => setTimeout(() => { if (desktop.matches && !item.contains(document.activeElement)) setDetails(item, false); }, 0));
      });
      all('.js-back', menu).forEach(button => button.addEventListener('click', () => {
        const parent = button.closest('.main-nav__child')?.parentElement;
        if (parent instanceof HTMLDetailsElement) { setDetails(parent, false); one(':scope > summary', parent)?.focus(); }
      }));
      nested.forEach(item => item.addEventListener('toggle', () => setDetails(item, item.open)));
      document.addEventListener('click', event => { if (!menu.contains(event.target)) close(); });
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && (disclosure.open || primary.some(item => item.open))) close(true); });
      document.addEventListener('focusin', event => { if (desktop.matches && !menu.contains(event.target)) closeChildren(); });
      desktop.addEventListener('change', sync);
      window.addEventListener('resize', position, { passive: true });
      window.addEventListener('scroll', position, { passive: true });
      sync();
    });
  }

  function initDisclosures(scope) {
    all('footer-menu > details, details-disclosure > details', scope).forEach(details => {
      if (details.dataset.referenceDisclosureReady) return;
      details.dataset.referenceDisclosureReady = 'true';
      const summary = one(':scope > summary', details);
      const footer = details.closest('footer-menu');
      const sync = () => {
        if (footer) setDetails(details, desktop.matches || !details.closest('.footer-col--collapsed'));
        else setDetails(details, details.open);
        if (summary && footer) summary.tabIndex = desktop.matches ? -1 : 0;
      };
      details.addEventListener('toggle', () => setDetails(details, details.open));
      if (footer) desktop.addEventListener('change', sync);
      sync();
    });
  }

  function initLanguageIndicator(scope) {
    all('.cc-header store-header', scope).forEach(header => {
      if (one('.reference-language', header)) return;
      const indicator = variant => {
        const label = document.createElement('span');
        label.className = `reference-language reference-language--${variant}`;
        label.setAttribute('aria-label', 'Langue du site : français');
        label.title = 'Français';
        const flag = document.createElement('img');
        flag.src = 'https://www.inshape-nutrition.com/cdn/shop/files/FR.svg?crop=center&height=48&v=1756112076&width=48';
        flag.width = 20;
        flag.height = 20;
        flag.alt = '';
        const text = document.createElement('span');
        text.textContent = 'FR';
        label.append(flag, text);
        return label;
      };
      one('.header__icons', header)?.prepend(indicator('desktop'));
      one('.header__logo', header)?.append(indicator('mobile'));
    });
  }

  function initHomeReviews(scope) {
    all('.jdgm-carousel-wrapper', scope).forEach(wrapper => {
      if (wrapper.dataset.referenceReviewsReady || wrapper.closest('[data-reference-product]')) return;
      const track = one('.jdgm-carousel__item-container', wrapper);
      const cards = all('.jdgm-carousel-item', wrapper);
      if (!track || !cards.length) return;
      wrapper.dataset.referenceReviewsReady = 'true';
      wrapper.id ||= 'reference-home-reviews';
      track.tabIndex = 0;
      track.setAttribute('aria-label', 'Avis clients, défilement horizontal');
      const controls = [[one('.jdgm-carousel__left-arrow', wrapper), -1], [one('.jdgm-carousel__right-arrow', wrapper), 1]];
      const update = () => controls.forEach(([control, direction]) => control?.setAttribute('aria-disabled', String(direction < 0 ? track.scrollLeft < 2 : track.scrollLeft >= track.scrollWidth - track.clientWidth - 2)));
      const move = direction => {
        const distance = cards.length > 1 ? cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left : track.clientWidth;
        track.scrollBy({ left: direction * distance, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      controls.forEach(([control, direction]) => {
        if (!control) return;
        control.setAttribute('aria-label', direction < 0 ? 'Avis précédents' : 'Avis suivants');
        control.addEventListener('click', () => move(direction));
        control.addEventListener('keydown', event => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); move(direction); } });
      });
      track.addEventListener('keydown', event => { if (event.target === track && ['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); } });
      track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      const section = wrapper.closest('.shopify-section') || wrapper;
      all('a[href^="javascript:"]', section).forEach(link => { link.href = '#' + wrapper.id; });
      update();
    });
  }

  function initCarousels(scope) {
    all('carousel-slider, slide-show', scope).forEach(host => {
      if (host.dataset.referenceCarouselReady || host.closest('[data-reference-product]')) return;
      const track = one(':scope > .slider, :scope > div > .slider, :scope > .slideshow', host);
      if (!track) return;
      const grid = one(':scope > .slider__grid, :scope > div > .slider__grid', track) || track;
      const slides = Array.from(grid.children).filter(item => item.matches('.slider__item, .slideshow__slide'));
      if (!slides.length) return;
      host.dataset.referenceCarouselReady = 'true';
      const slideShow = host.tagName === 'SLIDE-SHOW';
      const buttons = all('button[name="prev"], button[name="next"]', host).filter(button => button.closest('carousel-slider, slide-show') === host);
      const pages = all('.page-btn', host);
      let current = 0;
      let timer;
      let paused = false;
      let drag = null;
      let suppressClickUntil = 0;
      const left = slide => slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      const go = index => {
        current = slideShow ? (index + slides.length) % slides.length : Math.max(0, Math.min(slides.length - 1, index));
        track.scrollTo({ left: left(slides[current]), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const inactive = max < 3;
        host.toggleAttribute('inactive', inactive);
        let closest = Infinity;
        slides.forEach((slide, index) => { const distance = Math.abs(left(slide) - track.scrollLeft); if (distance < closest) { closest = distance; current = index; } });
        buttons.forEach(button => { button.disabled = inactive || (!slideShow && (button.name === 'prev' ? track.scrollLeft < 2 : track.scrollLeft > max - 2)); });
        if (slideShow) slides.forEach((slide, index) => { slide.classList.toggle('is-active', index === current); slide.setAttribute('aria-hidden', String(index !== current)); });
        pages.forEach((button, index) => button.setAttribute('aria-current', String(index === current)));
        one('.slideshow-nav__counter-current', host)?.replaceChildren(String(current + 1));
      };
      const auto = () => {
        clearInterval(timer);
        if (!slideShow || host.dataset.autoplay !== 'true' || slides.length < 2 || reducedMotion.matches || paused || document.hidden) return;
        timer = setInterval(() => go(current + 1), Math.max(2500, Number(host.dataset.speed || 5) * 1000));
      };
      buttons.forEach(button => button.addEventListener('click', event => { event.preventDefault(); go(current + (button.name === 'prev' ? -1 : 1)); }));
      pages.forEach((button, index) => button.addEventListener('click', () => go(index)));
      one('.autoplay-btn', host)?.addEventListener('click', event => { paused = !paused; event.currentTarget.classList.toggle('is-paused', paused); event.currentTarget.setAttribute('aria-label', paused ? 'Démarrer le diaporama' : 'Mettre le diaporama en pause'); auto(); });
      host.addEventListener('mouseenter', () => clearInterval(timer));
      host.addEventListener('mouseleave', auto);
      host.addEventListener('focusin', () => clearInterval(timer));
      host.addEventListener('focusout', () => { if (!host.contains(document.activeElement)) auto(); });
      document.addEventListener('visibilitychange', auto);
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('keydown', event => { if (event.target === track && ['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); go(current + (event.key === 'ArrowLeft' ? -1 : 1)); } });
      if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
      track.addEventListener('pointerdown', event => { if (event.pointerType === 'mouse' && event.button === 0) drag = { x: event.clientX, scroll: track.scrollLeft, moved: false }; });
      track.addEventListener('pointermove', event => {
        if (!drag) return;
        const delta = event.clientX - drag.x;
        if (Math.abs(delta) > 7) drag.moved = true;
        if (drag.moved) { event.preventDefault(); track.scrollLeft = drag.scroll - delta; track.classList.add('is-dragging'); }
      });
      const endDrag = () => { if (drag?.moved) suppressClickUntil = Date.now() + 350; drag = null; track.classList.remove('is-dragging'); };
      window.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('click', event => { if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); } }, true);
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      else window.addEventListener('resize', update, { passive: true });
      all('img', track).forEach(img => { if (!img.complete) img.addEventListener('load', update, { once: true }); });
      update();
      auto();
    });
  }

  function initCustomSelects(scope) {
    all('custom-select', scope).forEach(select => {
      if (select.closest('[data-reference-product]') || select.dataset.referenceSelectReady) return;
      const button = one('.custom-select__btn', select);
      const list = one('[role="listbox"]', select);
      const options = all('[role="option"]', select);
      if (!button || !list || !options.length) return;
      select.dataset.referenceSelectReady = 'true';
      const setOpen = open => { list.hidden = !open; list.classList.toggle('invisible', !open); list.setAttribute('aria-hidden', String(!open)); button.setAttribute('aria-expanded', String(open)); };
      const choose = option => {
        options.forEach(item => item.setAttribute('aria-selected', String(item === option)));
        const label = one('.text-start', button) || button.firstElementChild;
        if (label) label.textContent = option.textContent.trim();
        button.dataset.value = option.dataset.value;
        const native = one('select', select);
        if (native) { native.value = option.dataset.value; native.dispatchEvent(new Event('change', { bubbles: true })); }
        select.dispatchEvent(new CustomEvent('reference:select', { bubbles: true, detail: { value: option.dataset.value } }));
        setOpen(false);
        button.focus();
      };
      button.addEventListener('click', () => setOpen(list.hidden));
      options.forEach(option => { option.tabIndex = -1; option.addEventListener('click', () => choose(option)); });
      select.addEventListener('keydown', event => {
        if (event.key === 'Escape') { setOpen(false); button.focus(); }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); setOpen(true);
          const current = options.indexOf(document.activeElement);
          options[(current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length].focus();
        }
        if (event.key === 'Enter' && options.includes(document.activeElement)) { event.preventDefault(); choose(document.activeElement); }
      });
      document.addEventListener('click', event => { if (!select.contains(event.target)) setOpen(false); });
      setOpen(false);
    });
  }

  function initCollections(scope) {
    all('.main-products-grid', scope).forEach(container => {
      if (container.dataset.referenceFiltersReady) return;
      const results = one('.main-products-grid__results', container);
      const grid = results && one(':scope > ul.grid, :scope > .grid', results);
      const facets = one('facet-filters', container);
      if (!grid || !facets) return;
      container.dataset.referenceFiltersReady = 'true';
      const toolbar = document.getElementById('products-toolbar');
      const toggle = toolbar && one('.js-toggle-filters', toolbar);
      const filterContainer = facets.closest('.main-products-grid__filters');
      const form = one('form', facets);
      const describeCard = (element, index) => {
        const card = one('.card--product, product-card', element);
        const priceText = one('.price__current', element)?.textContent || '';
        const parsedPrice = Number(priceText.replace(/[^\d,.-]/g, '').replace(',', '.'));
        return { element, index, card, title: one('.card__title', element)?.textContent.trim() || '', price: element.dataset.referencePrice ? Number(element.dataset.referencePrice) / 100 : parsedPrice };
      };
      const cards = Array.from(grid.children).map(describeCard);
      let sort = 'manual';
      let loadAllPromise = null;
      const empty = document.createElement('p');
      empty.className = 'reference-filter-empty';
      empty.textContent = 'Aucun produit ne correspond à ces filtres.';
      empty.hidden = true;
      empty.setAttribute('role', 'status');
      results.append(empty);
      const apply = () => {
        const minValue = one('[name="filter.v.price.gte"]', facets)?.value || '';
        const maxValue = one('[name="filter.v.price.lte"]', facets)?.value || '';
        const min = minValue === '' ? 0 : Number(minValue);
        const max = maxValue === '' ? Infinity : Number(maxValue);
        const ordered = [...cards].sort((a, b) => sort === 'price-ascending' ? a.price - b.price : sort === 'price-descending' ? b.price - a.price : sort === 'title-ascending' ? a.title.localeCompare(b.title, 'fr') : sort === 'title-descending' ? b.title.localeCompare(a.title, 'fr') : a.index - b.index);
        let visible = 0;
        ordered.forEach(item => {
          item.element.hidden = !!item.card && Number.isFinite(item.price) && (item.price < min || item.price > max);
          if (item.card && !item.element.hidden) visible++;
          grid.append(item.element);
        });
        empty.hidden = visible > 0;
        all('.facets__footer .js-close-drawer', facets).forEach(button => { button.textContent = `Afficher ${visible} résultat${visible === 1 ? '' : 's'}`; });
        all('.js-clear-filter', facets).forEach(link => { link.hidden = minValue === '' && maxValue === ''; });
        const selected = one(`[name="sort_by"][value="${CSS.escape(sort)}"]`, facets);
        if (selected) selected.checked = true;
      };
      const applyAcrossPages = () => {
        apply();
        if (loadAllPromise || typeof container.referenceLoadNext !== 'function' || !one('.js-pagination-load-more, .js-load-more', container)) return;
        form?.setAttribute('aria-busy', 'true');
        loadAllPromise = (async () => {
          for (let page = 0; page < 30 && one('.js-pagination-load-more, .js-load-more', container); page++) {
            if (!await container.referenceLoadNext()) break;
          }
          apply();
        })().finally(() => { loadAllPromise = null; form?.removeAttribute('aria-busy'); });
      };
      const openFilters = open => {
        filterContainer?.classList.toggle('lg:js-hidden', !open);
        facets.toggleAttribute('open', open);
        facets.classList.toggle('is-open', open);
        facets.setAttribute('aria-hidden', String(!open));
        toggle?.setAttribute('aria-expanded', String(open));
        toggle?.classList.toggle('is-active', open);
        document.body.classList.toggle('reference-filters-open', open && !wide.matches);
        if (wide.matches) grid.className = open ? grid.dataset.filtersOpenClasses || grid.className : grid.dataset.filtersClosedClasses || grid.className;
        if (open && !wide.matches) one('.js-close-drawer', facets)?.focus();
      };
      toggle?.addEventListener('click', () => openFilters(toggle.getAttribute('aria-expanded') !== 'true'));
      all('.js-close-drawer', facets).forEach(button => button.addEventListener('click', () => { openFilters(false); toggle?.focus(); }));
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && facets.hasAttribute('open')) openFilters(false); });
      form?.addEventListener('submit', event => { event.preventDefault(); applyAcrossPages(); });
      form?.addEventListener('change', event => { if (event.target.name === 'sort_by') sort = event.target.value; applyAcrossPages(); });
      all('[name^="filter.v.price"]', facets).forEach(input => input.addEventListener('input', applyAcrossPages));
      all('price-range', facets).forEach(range => {
        const sliders = all('input[type="range"]', range);
        const numbers = all('input[type="number"]', range);
        sliders.forEach((input, index) => { input.tabIndex = 0; input.addEventListener('input', () => { if (numbers[index]) numbers[index].value = input.value; applyAcrossPages(); }); });
        numbers.forEach((input, index) => input.addEventListener('input', () => { if (sliders[index]) sliders[index].value = input.value || input.placeholder; }));
      });
      all('.js-clear-filter', facets).forEach(link => link.addEventListener('click', event => { event.preventDefault(); all('input[type="number"]', facets).forEach(input => { input.value = ''; }); all('input[type="range"]', facets).forEach((input, index) => { input.value = index ? input.max : input.min; }); apply(); }));
      toolbar?.addEventListener('reference:select', event => { sort = event.detail.value; applyAcrossPages(); });
      container.addEventListener('reference:pagination-appended', event => {
        const start = cards.length;
        cards.push(...event.detail.elements.map((element, index) => describeCard(element, start + index)));
        apply();
      });
      all('[name="layout"]', toolbar || document.createElement('div')).forEach(input => input.addEventListener('change', () => { if (input.checked) container.dataset.layout = input.value; }));
      wide.addEventListener('change', () => openFilters(false));
      openFilters(false);
    });
  }

  function initPagination(scope) {
    all('custom-pagination', scope).forEach(host => {
      if (host.dataset.referencePaginationReady || !one('.js-pagination-result', host)) return;
      host.dataset.referencePaginationReady = 'true';
      const totalMatch = one('.js-pagination-message', host)?.textContent.match(/(?:sur|of)\s+(\d+)/i);
      const expectedTotal = totalMatch ? Number(totalMatch[1]) : null;
      const syncProgress = () => {
        const count = all('.js-pagination-result', host).filter(element => element.closest('custom-pagination') === host).length;
        if (expectedTotal && count >= expectedTotal) {
          all('a.js-pagination-load-more, a.js-load-more', host).forEach(link => link.remove());
          all('.js-pagination', host).forEach(element => { element.dataset.isMoreResults = 'false'; });
          all('.js-pagination-message', host).forEach(element => { element.textContent = `Affichage ${expectedTotal} sur ${expectedTotal}`; });
          all('.js-pagination-bar', host).forEach(element => element.style.setProperty('--pagination-percent', '100%'));
        }
      };
      let loading = false;
      const load = async trigger => {
        if (!trigger || loading) return false;
        let url;
        try { url = new URL(trigger.getAttribute('href'), location.href); } catch { return false; }
        if (url.origin !== location.origin) { notice('Cet aperçu ne charge que les pages du brouillon de cette boutique.', trigger); return false; }
        const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewTheme && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', previewTheme);
        const first = one('.js-pagination-result', host);
        const list = first?.parentElement;
        const pagination = trigger.closest('.js-pagination');
        if (!list || !pagination) return false;
        loading = true;
        host.setAttribute('aria-busy', 'true');
        trigger.setAttribute('aria-disabled', 'true');
        try {
          const response = await fetch(url.href, { method: 'GET', credentials: 'same-origin', redirect: 'error', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(20000) });
          if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Page du brouillon indisponible');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextHost = all('custom-pagination', parsed).find(element => !!one('.js-pagination-result', element));
          const incoming = nextHost && all('.js-pagination-result', nextHost).filter(element => element.closest('custom-pagination') === nextHost);
          const nextPagination = nextHost && one('.js-pagination', nextHost);
          if (!incoming?.length || !nextPagination || !parsed.documentElement.hasAttribute('data-reference-draft')) throw new Error('Réponse non reconnue comme aperçu du brouillon');
          const existing = new Set(all(':scope > .js-pagination-result', list).map(element => one('.card__title a, .card-link, .js-prod-link', element)?.getAttribute('href')).filter(Boolean));
          const appended = [];
          incoming.forEach(element => {
            const key = one('.card__title a, .card-link, .js-prod-link', element)?.getAttribute('href');
            if (key && existing.has(key)) return;
            all('script,iframe,object,embed', element).forEach(node => node.remove());
            all('*', element).forEach(node => Array.from(node.attributes).forEach(attribute => { if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name); }));
            const clone = document.importNode(element, true);
            list.append(clone);
            appended.push(clone);
            if (key) existing.add(key);
          });
          const replacement = document.importNode(nextPagination, true);
          pagination.replaceWith(replacement);
          syncProgress();
          host.dispatchEvent(new CustomEvent('reference:pagination-appended', { bubbles: true, detail: { elements: appended } }));
          init(host);
          const status = one('.js-pagination-message', replacement);
          if (status) { status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); }
          return appended.length > 0;
        } catch (error) {
          notice('Impossible de charger la suite de cet aperçu. Tu peux réessayer, aucun contenu de la boutique n’a été modifié.', trigger);
          console.warn('[Reference draft] Pagination:', error.message);
          return false;
        } finally {
          loading = false;
          host.removeAttribute('aria-busy');
          trigger.removeAttribute('aria-disabled');
        }
      };
      host.referenceLoadNext = () => load(one('a.js-pagination-load-more, a.js-load-more', host));
      host.addEventListener('reference:pagination-appended', syncProgress);
      host.addEventListener('click', event => {
        const trigger = event.target.closest('a.js-pagination-load-more, a.js-load-more');
        if (!trigger || trigger.closest('custom-pagination') !== host) return;
        event.preventDefault();
        void load(trigger);
      });
      syncProgress();
    });
  }

  const comparedProducts = new Map();
  let compareDictionary = null;
  let compareDictionaryPromise = null;
  let compareDialog;
  let compareButton;
  let compareReturnFocus;

  function compareProductFromCard(input) {
    const card = input.closest('product-card, .card--product');
    if (!card) return null;
    const link = one('.card__title a, .card-link, .js-prod-link', card);
    const href = link?.getAttribute('href');
    const image = one('.card__media img', card);
    const handle = (input.dataset.productUrl || href || '').match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle || !href || new URL(href, location.href).origin !== location.origin) return null;
    return {
      handle,
      title: one('.card__title', card)?.textContent.trim() || card.dataset.referenceTitle || handle,
      price: one('.price__current', card)?.textContent.replace(/Prix\s+(?:habituel|réduit)/gi, '').replace(/\s+/g, ' ').trim() || 'Prix non affiché',
      image: image?.currentSrc || image?.src || '',
      href
    };
  }

  function comparisonNode(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function updateCompareControls() {
    if (!compareButton) return;
    compareButton.textContent = `Comparer (${comparedProducts.size})`;
    compareButton.hidden = comparedProducts.size === 0;
    all('.js-compare-checkbox').forEach(input => {
      const handle = (input.dataset.productUrl || '').match(/\/products\/([^/?#]+)/)?.[1];
      input.checked = comparedProducts.has(handle);
    });
    if (compareDialog?.open) renderComparison();
  }

  function renderComparison() {
    if (!compareDialog) return;
    const area = one('.reference-compare-area', compareDialog);
    area.replaceChildren();
    area.dataset.compareCount = String(comparedProducts.size);
    const products = [...comparedProducts.values()];
    const row = (className, render) => {
      const element = comparisonNode('div', `compare-row ${className}`);
      products.forEach(product => {
        const column = comparisonNode('div', 'compare-col');
        column.dataset.compareHandle = product.handle;
        render(column, product);
        element.append(column);
      });
      area.append(element);
    };
    row('compare-row--image', (column, product) => {
      const remove = comparisonNode('button', 'reference-compare-remove', 'Retirer');
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Retirer ' + product.title + ' de la comparaison');
      remove.addEventListener('click', () => {
        comparedProducts.delete(product.handle);
        if (!comparedProducts.size) compareDialog.close();
        updateCompareControls();
        one('.reference-compare-remove, .reference-compare-close', compareDialog)?.focus();
      });
      const link = comparisonNode('a', 'reference-compare-image');
      link.href = product.href;
      const image = comparisonNode('img');
      image.src = product.image;
      image.alt = product.title;
      image.width = 280;
      image.height = 280;
      link.append(image);
      column.append(remove, link);
    });
    row('compare-row--title', (column, product) => {
      const title = comparisonNode('a', 'h5 reference-compare-title', product.title);
      title.href = product.href;
      column.append(title);
    });
    row('compare-row--price', (column, product) => {
      column.append(comparisonNode('div', 'compare-label', 'Prix affiché'), comparisonNode('strong', 'price__current', product.price));
    });
    if (compareDictionary) {
      row('compare-row--type', (column, product) => {
        column.append(comparisonNode('div', 'compare-label font-bold', 'Type de produit'), comparisonNode('div', 'compare-value', compareDictionary[product.handle]?.type || 'Non renseigné'));
      });
      row('compare-row--variants', (column, product) => {
        const options = compareDictionary[product.handle]?.options || [];
        if (!options.length) { column.append(comparisonNode('div', 'compare-value', 'Format unique')); return; }
        options.forEach(option => {
          const values = option.values.map(value => String(value).replace(/\s*[|I]\s*[-−]\d+%/g, ''));
          column.append(comparisonNode('div', 'compare-label font-bold', option.name), comparisonNode('div', 'compare-value', [...new Set(values)].join(', ')));
        });
      });
    }
    row('compare-row--link', (column, product) => {
      const link = comparisonNode('a', 'btn btn--primary', 'Voir le produit');
      link.href = product.href;
      column.append(link);
    });
    const instruction = one('.reference-compare-instruction', compareDialog);
    instruction.textContent = products.length < 2 ? 'Sélectionne au moins deux produits pour les comparer.' : 'Compare jusqu’à quatre produits. Les prix affichés correspondent aux cartes du catalogue.';
  }

  function initComparison(scope) {
    if (!compareButton && one('.js-compare-checkbox', scope)) {
      compareButton = comparisonNode('button', 'btn btn--compare reference-compare-button', 'Comparer');
      compareButton.type = 'button';
      compareButton.hidden = true;
      compareButton.setAttribute('aria-controls', 'reference-compare-dialog');
      compareButton.setAttribute('aria-haspopup', 'dialog');
      compareDialog = comparisonNode('dialog', 'reference-compare-dialog compare-modal');
      compareDialog.id = 'reference-compare-dialog';
      compareDialog.setAttribute('aria-labelledby', 'reference-compare-title');
      const panel = comparisonNode('div', 'modal__window bg-theme-bg text-theme-text reference-compare-panel');
      const toolbar = comparisonNode('div', 'reference-compare-toolbar');
      const heading = comparisonNode('h2', 'h4', 'Comparateur de produits');
      heading.id = 'reference-compare-title';
      const clear = comparisonNode('button', 'link reference-compare-clear', 'Tout retirer');
      clear.type = 'button';
      clear.addEventListener('click', () => { comparedProducts.clear(); compareDialog.close(); updateCompareControls(); });
      const close = comparisonNode('button', 'reference-compare-close', '×');
      close.type = 'button';
      close.setAttribute('aria-label', 'Fermer le comparateur');
      close.addEventListener('click', () => compareDialog.close());
      toolbar.append(heading, clear, close);
      const instruction = comparisonNode('p', 'reference-compare-instruction');
      const area = comparisonNode('div', 'compare-container reference-compare-area');
      panel.append(toolbar, instruction, area);
      compareDialog.append(panel);
      document.body.append(compareButton, compareDialog);
      compareButton.addEventListener('click', async () => {
        if (comparedProducts.size < 2) { notice('Coche un deuxième produit pour ouvrir le comparateur.', compareButton); return; }
        compareReturnFocus = document.activeElement;
        renderComparison();
        compareDialog.showModal();
        close.focus();
        if (!compareDictionary && compareDataURL) {
          compareDictionaryPromise ||= fetch(compareDataURL, { method: 'GET', headers: { Accept: 'application/json' } })
            .then(response => { if (!response.ok) throw new Error('Données complémentaires indisponibles'); return response.json(); })
            .then(data => { compareDictionary = data; if (compareDialog.open) renderComparison(); })
            .catch(() => { compareDictionaryPromise = null; });
          await compareDictionaryPromise;
        }
      });
      compareDialog.addEventListener('close', () => {
        const target = compareReturnFocus?.isConnected && !compareReturnFocus.hidden ? compareReturnFocus : document.getElementById('compare-switch');
        target?.focus?.();
      });
      // Native <dialog> manages the modal focus trap and Escape key.
    }
    if (!compareButton) return;
    all('.js-compare-checkbox', scope).forEach(input => {
      if (input.dataset.referenceCompareReady) return;
      input.dataset.referenceCompareReady = 'true';
      input.addEventListener('change', () => {
        const product = compareProductFromCard(input);
        if (!product) { input.checked = false; notice('Cette fiche ne peut pas être ajoutée au comparateur de cet aperçu.', input); return; }
        if (input.checked) {
          if (!comparedProducts.has(product.handle) && comparedProducts.size >= 4) { input.checked = false; notice('Le comparateur accepte quatre produits maximum. Retire un produit avant d’en ajouter un autre.', input); return; }
          comparedProducts.set(product.handle, product);
        } else comparedProducts.delete(product.handle);
        updateCompareControls();
      });
    });
    const toggle = document.getElementById('compare-switch');
    if (toggle && !toggle.dataset.referenceCompareReady) {
      toggle.dataset.referenceCompareReady = 'true';
      const apply = () => {
        all('.main-products-grid').forEach(container => { container.dataset.compare = String(toggle.checked); });
        all('.card__compare').forEach(control => { control.hidden = !toggle.checked; });
        all('.products-toolbar__compare').forEach(control => control.classList.add('transition-ready'));
        document.documentElement.classList.add('reference-compare-enabled');
      };
      toggle.addEventListener('change', apply);
      apply();
    }
    if (toggle && !toggle.checked) all('.card__compare', scope).forEach(control => { control.hidden = true; });
    updateCompareControls();
  }

  function init(scope = document) {
    if (!isDraft()) return;
    document.documentElement.classList.add('js', 'reference-ready');
    document.documentElement.classList.remove('no-js', 'cc-animate-enabled');
    initMenus(scope);
    initLanguageIndicator(scope);
    initDisclosures(scope);
    initCarousels(scope);
    initHomeReviews(scope);
    initCustomSelects(scope);
    initCollections(scope);
    initPagination(scope);
    initComparison(scope);
    if (document.documentElement.dataset.gosEnabled !== 'true') all('[data-cart-drawer-open]', scope).forEach(link => { link.setAttribute('aria-controls', 'CartDrawer'); link.setAttribute('aria-expanded', 'false'); });
  }

  window.ReferenceDraft = Object.freeze({ init, notice, isDraft, checkoutURL });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('reference:content-ready', event => init(event.target));
})();
